import type { ReactNode } from 'react';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { usePageContext } from 'Context/PageContext';
import { useAppContext } from 'Context/ReactAppContext';
import { setDataLayer } from 'Tracking/tracking-api';
import { trackLastVisitedProducts } from 'Tracking/last-visited-tracking-api';
import { PLE, TRACK_EVENT_NAMES, VOUCHER_CODE, VOUCHER_DISCOUNT } from 'Tracking/constants';
import { getLeadScore } from 'Store/lead-info/leadInfo.selector';
import cookie from 'js-cookie';
import { getConnectionStatus, getNeolaneId, isPBFLandingPage } from 'Tracking/utils';
import appConfig from 'Config';
import { userInfoSelector } from 'Store/user-info/userInfo.selectors';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
type Props = {
  children?: ReactNode;
  pageName?: string;
  siteSection?: string;
  track?: () => void;
};

const TrackingComponent = ({ children, track, ...props }: Props) => {
  const appContext = useAppContext();
  const pageContext = usePageContext();
  const leadScoring = useSelector(getLeadScore);
  const { asPath } = useRouter();
  // in some cases (like the NotFoundView) the siteSection's value cannot be dynamically built
  // from the pageType and attached to the pageContext,
  // so we need to declare it in the props directly but this should be avoided
  const siteSection = props.siteSection || pageContext.siteSection;
  const { countryCode, countryName, currency, googleTrackingId, language, locale } = appContext;
  const profile: UserInfo = useSelector(userInfoSelector);
  // add GA4 container support for prebooking form landing pages add a pageview event
  const ga4PageViewParams = {
    page_name: 'landing_page_prebookingform',
    site_section: siteSection,
    user: {
      connection_status: getConnectionStatus(appContext),
      neolane_id: getNeolaneId(appContext),
      visitor_guid: googleTrackingId,
      full_visitor_id: googleTrackingId,
      environment: appConfig.get('tracking.environment'),
      currency,
      locale,
      country_code: countryCode,
      country_language: language,
      country_name: countryName,
      lead_scoring_range: leadScoring,
      fidelity_status: profile?.loyaltyProgram?.status || '',
    },
  };
  const { trackEvent } = useTrackEvent();
  useEffect(() => {
    trackLastVisitedProducts(pageContext.pageType, {
      // @ts-expect-error TODO(flow-migration)
      id: props.resortCode,
      be: false,
      timestamp: Date.now(),
    });
  }, [props.pageName]);
  useEffect(() => {
    // pageId is not actually needed here, since the categoryGA is only used for events
    // not for page loading (the analytics team doesn't care), but it makes our API more
    // consistent
    setDataLayer(
      {
        ...props,
        siteSection,
        ...(siteSection && siteSection.includes(PLE)
          ? {
              BE_customerStatus: appContext.customerStatus,
            }
          : undefined),
        ...(leadScoring
          ? {
              leadScoring,
            }
          : null),
      },
      appContext,
      pageContext.pageId,
    );
    const isPBFLanding = isPBFLandingPage(asPath);

    if (siteSection === 'Shopping_Landing' && isPBFLanding) {
      trackEvent(TRACK_EVENT_NAMES.PAGE_VIEW, ga4PageViewParams);
    } else {
      track && track();
    }

    cookie.remove(VOUCHER_DISCOUNT);
    cookie.remove(VOUCHER_CODE);
  }, [props.pageName]);
  return children ?? null;
};

export default TrackingComponent;
